import * as React from "react";
import {
  Unstable_NumberInput as BaseNumberInput,
  NumberInputProps,
} from "@mui/base/Unstable_NumberInput";
import { styled } from "@mui/system";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

const NumberInput = React.forwardRef(function CustomNumberInput(
  { value, readOnly, step, ...props }: any,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const handleMouseDown = (event: React.MouseEvent) => {
    if (readOnly) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  return (
    <BaseNumberInput
      slots={{
        root: StyledInputRoot,
        input: StyledInput,
        incrementButton: StyledButton,
        decrementButton: StyledButton,
      }}
      slotProps={{
        incrementButton: {
          children: <AddIcon fontSize="small" />,
          className: "increment",
          style: readOnly ? { display: "none" } : undefined,
        },
        decrementButton: {
          children: <RemoveIcon fontSize="small" />,
          style: readOnly ? { display: "none" } : undefined,
        },
      }}
      {...props}
      step={step}
      value={value}
      ref={ref}
      readOnly={readOnly}
      onMouseDown={handleMouseDown}
    />
  );
});

const blue = {
  100: "#daecff",
  200: "#b6daff",
  300: "#66b2ff",
  400: "#3399ff",
  500: "#007fff",
  600: "#0072e5",
  700: "#0059B2",
  800: "#004c99",
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const green: any = {
  100: "#c8e6c9",
  200: "#a5d6a7",
  300: "#81c784",
  400: "#66bb6a",
  500: "#4caf50",
  600: "#43a047",
  700: "#388e3c",
  800: "#2e7d32",
};

const StyledInputRoot = styled("div")(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-weight: 400;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[500]};
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
`
);

const StyledInput = styled("input")(
  ({ theme }) => `
    font-size: 0.875rem;
    font-family: inherit;
    font-weight: 400;
    line-height: 1.375;
    color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
    border: 1px solid ${
      theme.palette.mode === "dark"
        ? theme.palette.primary.main
        : theme.palette.primary.main
    };  // Set border color to green[500]
    box-shadow: 0px 2px 4px ${
      theme.palette.mode === "dark" ? "rgba(0,0,0, 0.5)" : "rgba(0,0,0, 0.05)"
    };
    border-radius: 8px;
    margin: 0 8px;
    padding: 10px 12px;
    outline: 0;
    min-width: 0;
    width: 8rem;
    text-align: center;
    
    &:hover {
      border-color: ${
        theme.palette.primary.main
      };  // Set hover border color to green[400]
    }
  
    &:focus {
      border-color: ${
        theme.palette.primary.main
      };  // Set focus border color to green[400]
      box-shadow: 0 0 0 0.5px ${
        theme.palette.mode === "dark"
          ? theme.palette.primary.main
          : theme.palette.primary.main
      };
    }
  
    &:focus-visible {
      outline: 0;
    }
  `
);

const StyledButton = styled("button")(
  ({ theme }: any) => `
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    box-sizing: border-box;
    line-height: 1.5;
    border: 1px solid;
    border-radius: 999px;
    border-color: ${
      theme.palette.mode === "dark"
        ? theme.palette.primary.main
        : theme.palette.primary.main
    };  // Set border color to green[500]
   
    color: ${
      theme.palette.mode === "dark" ? grey[200] : green[900]
    };  // Set text color to green[900]
    width: 32px;
    height: 32px;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 120ms;
  
    &:hover {
      cursor: pointer;
      background: ${
        theme.palette.mode === "dark"
          ? theme.palette.primary.main
          : theme.palette.primary.main
      };  // Set hover background color to green[500]
      border-color: ${
        theme.palette.mode === "dark"
          ? theme.palette.primary.main
          : theme.palette.primary.main
      };  // Set hover border color to green[400]
      color: ${grey[50]};
    }
  
    &:focus-visible {
      outline: 0;
    }
  
    &.increment {
      order: 1;
    }
  `
);

export default NumberInput;
